import { Toast } from '@florencelabs/toast';
import DOMPurify from 'dompurify';
import { ReactElement, useRef, useEffect } from 'react';
import { useForm, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAppointment } from '@provider-directory/contexts/AppointmentContext';
import {
  useDecisionTree,
  type DecisionTreeType,
  getSchedulingFlowType,
} from '@provider-directory/contexts/DecisionTreeContext';
import { useProvider } from '@provider-directory/contexts/ProviderContext';
import { useSharedDecisionTree } from '@provider-directory/contexts/SharedDecisionTreeContext';
import { isElementInViewport } from '@provider-directory/functions/dom';
import { useIsDesktop } from '@provider-directory/hooks/useBreakpoints';
import {
  DecisionTreeMode,
  getNavigationOptions,
  getDecisionTreeLocation,
  type SceneSlug,
  type DecisionTreeQuestionSceneProps,
  isValueAllowed,
  parseTooltipContent,
} from '@provider-directory/models/decision-tree.model';
import { useAnalytics } from '../analytics/analytics-provider';
import { RovingFocusRadioTabGroup } from '../forms/roving-focus-radio-tab-group';
import {
  useOrgContext,
  useOrgContextBasedOnHospitalAssociations,
} from '../org-context/org-context-provider';
import { getSceneActionByType } from './decision-tree-actions';
import { DecisionTreeContainer } from './decision-tree-container';
import { DecisionTreeHeader } from './decision-tree-header';
import { getSceneValidationByType } from './decision-tree-validations';

export interface DecisionTreeQuestionProps
  extends DecisionTreeQuestionSceneProps {
  onNext: (slug: SceneSlug, options?: { redirectLink?: string }) => void;
  onBack: (slug: SceneSlug | null) => void;
  autoFocusHeading?: boolean;
}

export const DecisionTreeQuestion = ({
  name,
  text,
  warning,
  specialty = '',
  options,
  action: sceneAction,
  onNext,
  autoFocusHeading,
}: DecisionTreeQuestionProps): ReactElement => {
  const { provider } = useProvider();
  const { appointment } = useAppointment();
  const { runtimeConfig } = useOrgContext();
  const { key, name: healthSystemName } =
    useOrgContextBasedOnHospitalAssociations(provider?.hospitalAssociations);

  const { decisionTree, specialtyBranch, mode, updateDecisionTree } =
    useDecisionTree();
  const { updateAnswer, updateLastViewedScene } = useSharedDecisionTree();
  const { t } = useTranslation(key);
  const analytics = useAnalytics();
  const errorMessageRef = useRef<HTMLDivElement>(null);
  const isDesktop = useIsDesktop();

  const { handleSubmit, control, formState, reset } = useForm<DecisionTreeType>(
    {
      mode: 'onTouched',
      defaultValues: decisionTree,
      shouldFocusError: false,
    },
  );

  useEffect(() => {
    reset(decisionTree);
  }, [decisionTree, reset]);

  const { field } = useController({
    name,
    control,
    rules: {
      validate: (val) => isValueAllowed(options, val) || 'selectAnOption',
    },
  });

  const scrollToErrorIfNotVisible = () => {
    if (errorMessageRef.current) {
      const modalRoot = document.querySelector('.adaptive-height');
      if (modalRoot) {
        const isVisible = isElementInViewport(
          errorMessageRef.current,
          modalRoot,
        );

        if (!isVisible) {
          const prefersReducedMotion = window.matchMedia(
            '(prefers-reduced-motion: reduce)',
          ).matches;
          errorMessageRef.current.scrollIntoView({
            behavior: prefersReducedMotion ? 'auto' : 'smooth',
          });
        }
      }
    }
  };

  const onInvalid = () => {
    setTimeout(scrollToErrorIfNotVisible, 50);
  };

  const submit = (data: DecisionTreeType) => {
    let currentDecisionTree: DecisionTreeType = { ...decisionTree, ...data };

    const selectedOption = options.find(
      (option) => option.value === data[name],
    );

    if (!selectedOption) {
      updateDecisionTree(currentDecisionTree);
      return;
    }

    updateAnswer(name, data[name]);

    if (sceneAction) {
      const actionFunc = getSceneActionByType(sceneAction);
      const actionResult = actionFunc({
        decisionTree: currentDecisionTree,
        selectedOption,
        updateDecisionTree,
      });

      currentDecisionTree = actionResult;
    }

    if (selectedOption.action) {
      const actionFunc = getSceneActionByType(selectedOption.action);
      const actionResult = actionFunc({
        decisionTree: currentDecisionTree,
        selectedOption,
        updateDecisionTree,
      });

      currentDecisionTree = actionResult;
    }

    if (provider) {
      // Send analytics event BEFORE validation check to ensure we track all answers
      analytics.dTreeAnswerSubmittedEvent({
        questionId: name,
        answer: selectedOption.value,
        provider,
        location: getDecisionTreeLocation(provider, appointment.departmentId)!,
        specialtyBranch,
        reasonForVisit: currentDecisionTree.rfvKey,
        schedulingFlowType: getSchedulingFlowType(),
      });
    }

    if (
      selectedOption.validation &&
      mode !== DecisionTreeMode.RedirectOnRfvSelect
    ) {
      updateDecisionTree(currentDecisionTree);

      const validations = Array.isArray(selectedOption.validation)
        ? selectedOption.validation
        : [selectedOption.validation];

      for (const { type, failedValidationScene } of validations) {
        const validationFunction = getSceneValidationByType(type);

        const isValidated = validationFunction({
          provider,
          reasonForVisit: currentDecisionTree.rfvId,
          decisionTreeState: currentDecisionTree,
          associationGroupCodes: runtimeConfig.associationGroupCodes,
        });

        if (!isValidated) {
          updateLastViewedScene(name);
          onNext(failedValidationScene);
          return;
        }
      }

      onNext(selectedOption.nextScene);
      return;
    }

    updateDecisionTree(currentDecisionTree);

    const navigateNextOptions = getNavigationOptions(
      selectedOption.nextScene,
      currentDecisionTree.rfvId,
      mode,
    );

    onNext(selectedOption.nextScene, navigateNextOptions);
  };

  const translatedOptions = options.map((option) => ({
    value: option.value,
    label: t(option.label),
    subCopy: option.subCopy ? t(option.subCopy) : undefined,
    datatestid: `option-${option.value}`,
    tooltip: option.tooltip
      ? {
          content: (
            <span
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  parseTooltipContent(option.tooltip, t),
                ),
              }}
            />
          ),
          variant: isDesktop ? ('dark' as const) : undefined,
          ariaLabel: option.tooltipAriaLabel
            ? t(option.tooltipAriaLabel)
            : undefined,
        }
      : undefined,
  }));

  return (
    <DecisionTreeContainer>
      <div data-testid={`decision-tree-container-${name}`} className={'w-full'}>
        <DecisionTreeHeader autoFocus={autoFocusHeading}>
          {t(text, {
            healthSystem: healthSystemName,
            specialty: t(specialty),
          })}
        </DecisionTreeHeader>
        <div>
          <form
            id={name}
            onSubmit={handleSubmit(submit, onInvalid)}
            noValidate={true}
          >
            <RovingFocusRadioTabGroup
              data-testid={'radio-tab-group'}
              className={'flex w-full flex-col'}
              aria-label={t(text, {
                healthSystem: healthSystemName,
                specialty: t(specialty),
              })}
              isRequired={true}
              errorMessageRef={errorMessageRef}
              onChange={field.onChange}
              options={translatedOptions}
              /* eslint-disable */
              errorMessage={t(formState.errors[name]?.message!)}
              value={field.value}
              optionLabelClassName={'font-semibold'}
            />
          </form>
          <div className={'pt-3'}>
            {warning ? (
              <Toast
                variant={'warning'}
                layout={'multiLine'}
                isDismissible={false}
                description={t(warning)}
                cornerRadius={'medium'}
              />
            ) : null}
          </div>
        </div>
      </div>
    </DecisionTreeContainer>
  );
};
